import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { ArrowLeft, Loader2, CheckCircle, Lock, Eye, EyeOff, XCircle, KeyRound } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Check token validity on mount
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setTokenError('Lien de réinitialisation invalide');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/auth/check-reset-token?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserInfo(response.data);
        }
      } catch (error) {
        setTokenError(error.response?.data?.detail || 'Lien de réinitialisation invalide ou expiré');
      } finally {
        setLoading(false);
      }
    };
    
    checkToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      toast.success('Mot de passe réinitialisé avec succès !');
      setSuccess(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la réinitialisation'));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2E4A3D] to-[#1a2e25] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-[#2E4A3D] mx-auto mb-4" />
              <p className="text-stone-600">Vérification du lien...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2E4A3D] to-[#1a2e25] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-serif text-red-700">
              Lien invalide
            </CardTitle>
          </CardHeader>
          
          <CardContent className="text-center space-y-4">
            <p className="text-stone-600">
              {tokenError}
            </p>
            
            <div className="space-y-3 pt-4">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full btn-primary"
                data-testid="request-new-link"
              >
                Demander un nouveau lien
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Retour à la connexion
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2E4A3D] to-[#1a2e25] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-serif text-green-700">
              Mot de passe réinitialisé !
            </CardTitle>
          </CardHeader>
          
          <CardContent className="text-center space-y-4">
            <p className="text-stone-600">
              Votre mot de passe a été modifié avec succès.
            </p>
            <p className="text-stone-500 text-sm">
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
            
            <Button
              onClick={() => navigate('/login')}
              className="w-full btn-primary mt-4"
              data-testid="go-to-login"
            >
              Se connecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid token - show password reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E4A3D] to-[#1a2e25] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Link to="/login" className="inline-flex items-center text-sm text-stone-500 hover:text-[#2E4A3D] mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à la connexion
          </Link>
          <div className="w-16 h-16 bg-[#2E4A3D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-[#2E4A3D]" />
          </div>
          <CardTitle className="text-2xl font-serif text-[#2E4A3D]">
            Nouveau mot de passe
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-green-800">
              Bonjour <strong>{userInfo?.prenom}</strong>, choisissez votre nouveau mot de passe.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  className="pl-10 pr-10"
                  required
                  minLength={6}
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmez votre mot de passe"
                  className="pl-10"
                  required
                  data-testid="confirm-password"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={submitting}
              data-testid="reset-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Réinitialisation...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Réinitialiser le mot de passe
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
